// Device-local personalization. The affinity profile lives ONLY in the
// visitor's localStorage — no server-side profiles, no third-party trackers,
// no consent banner required. Geo comes from /api/geo (Cloudflare edge,
// approximate city, not stored server-side).
import type { Event } from '../types';

export interface Geo {
  city: string | null;
  country: string | null;
  lat: number | null;
  lon: number | null;
}

interface Profile {
  categories: Record<string, number>;
  cities: Record<string, number>;
  artists: Record<string, number>;
}

const KEY = 'tixinfo.affinity.v1';
const GEO_KEY = 'tixinfo.geo.v1';
const EMPTY: Profile = { categories: {}, cities: {}, artists: {} };

const load = (): Profile => {
  try {
    return { ...EMPTY, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') };
  } catch {
    return { ...EMPTY };
  }
};

const bump = (bag: Record<string, number>, k: string | undefined, by: number) => {
  if (!k) return;
  bag[k] = Math.min((bag[k] ?? 0) + by, 30); // cap so old interests can be overtaken
};

const save = (p: Profile) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable — personalization simply stays off */
  }
};

/** Record that the visitor looked at an event page (weight 2) or a result (weight 1). */
export const trackInterest = (e: Event, weight: 1 | 2 = 2) => {
  const p = load();
  bump(p.categories, e.category, weight);
  bump(p.cities, e.city, weight);
  bump(p.artists, e.artist, weight);
  save(p);
};

export const getProfile = (): Profile => load();

/** 0..4 — how well an event matches what this browser has looked at. */
export const affinityScore = (e: Event, p: Profile): number => {
  let s = 0;
  if (e.artist && (p.artists[e.artist] ?? 0) > 0) s += 2;
  if ((p.cities[e.city] ?? 0) > 1) s += 1;
  if ((p.categories[e.category] ?? 0) > 2) s += 1;
  return s;
};

const R = 6371;
export const distanceKm = (aLat: number, aLon: number, bLat: number, bLon: number) => {
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLon - aLon) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/** 0..3 — closer events score higher; 0 when geo or event coords unknown. */
export const proximityScore = (e: Event, geo: Geo | null): number => {
  if (!geo?.lat || !geo?.lon || (!e.lat && !e.lng)) return 0;
  const d = distanceKm(geo.lat, geo.lon, e.lat, e.lng);
  if (d < 80) return 3;
  if (d < 300) return 2;
  if (d < 800) return 1;
  return 0;
};

/** This session's location answer, if it has one, without asking (the boot reads it). */
export const cachedGeo = (): Geo | null => {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    return cached ? (JSON.parse(cached) as Geo) : null;
  } catch {
    return null;
  }
};

const readGeo = async (): Promise<Geo | null> => {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) return JSON.parse(cached);
    const res = await fetch('/api/geo');
    if (!res.ok) return null;
    const geo: Geo = await res.json();
    sessionStorage.setItem(GEO_KEY, JSON.stringify(geo));
    return geo.lat ? geo : null;
  } catch {
    return null;
  }
};

let geoAsk: Promise<Geo | null> | null = null;

/** Fetch approximate location once per session; fail-soft to null (e.g. local dev). The callers
 *  of one moment share one request and one answer — the language and the currency defaults, the
 *  page's own — so what they change from it lands in one render. */
export const getGeo = (): Promise<Geo | null> => {
  if (!geoAsk) {
    const ask = readGeo();
    geoAsk = ask;
    void ask.finally(() => {
      if (geoAsk === ask) geoAsk = null;
    });
  }
  return geoAsk;
};
